import React, { useRef, useState } from 'react';
import './documentacion-veh.css';

import { ConfirmActionDialog } from '../../common/confirm-dialog/confirm-action-dialog';
import { formatDateTimeFront } from '../../../utils/dateFormatter';
import { validarArchivo } from '../../../utils/validarArchivo';

import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { ConfirmDialog } from 'primereact/confirmdialog';

/**
 * TODO:
 * 1. Agregar documento X, debe haber una opcion de agregar documento, 
 * que permita subir cualquier documento de tipo pdf indicando:
 * nombre del documento, emision, vencimiento. 
 * (Eso crearia un nuevo registro como los de DOCS_COMUNES)
 * 2. Modificar la agregacion actual de documentos para que al agregar el documento 
 * pida: emision y vencimiento. y bueno eso si no existe pedir nombre si ya exist mostral el nombre que tiene.
 */

const DocumentacionVeh = React.memo(({ vehiculo, documentos = {}, permisos = {}, onDescargar, onCargar, onActualizar, onEliminar, showToast }) => {
  const toast = useRef(null);
  const fileRef = useRef(null);
  const docKeyRef = useRef(null);

  const [subiendoKey, setSubiendoKey] = useState(null);
  
  if (!vehiculo) {
    return (
      <div className='p-1'>
        <div className="alert alert-primary" role="alert">
          <h5 className="alert-heading">Documentación</h5>
          <p>En este apartado podrá encontrar toda documentación asociada al vehículo seleccionado.</p>
          <hr></hr>

          <p>
            Si cuenta con los permisos necesarios, podrá actualizar los archivos haciendo click en el botón 
            <b> "Actualizar Documentación" </b> ubicado en la parte superior derecha de esta sección.
          </p>
          
          <p>Esta función le permitará cargar y gestionar los documentos asociados al vehículo:</p>
          
          <ul className="pl-4">
            <li>Podrá subir documentos como el permiso de circulación, revisión técnica, seguro obligatorio, entre otros.</li>
            <li>Podrá eliminar documentos que ya no sean relevantes o estén desactualizados.</li>
            <li>Podrá visualizar los documentos actuales asociados al vehículo.</li>
          </ul>

          <hr></hr>
          
          <p>ADVERTENCIA: Los cambios que realice no se pueden deshacer.</p>
          <b>Verifique los documentos que va a actualizar antes de subirlos a la plataforma.</b>
          
        </div>
      </div>
    );
  }

  const { obligatorios, especificos } = getDocumentos(vehiculo.modelo, vehiculo.tipo);
  const todos = [...obligatorios, ...especificos];
  const cargados = todos.filter(d => documentos[d.key]).length;
  
  const abrirSelector = (key) => {
    docKeyRef.current = key;
    fileRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    
    if (!file || !docKeyRef.current) return;
    
    // Verifica MIME, Extensión, Tamaño.
    const validacion = validarArchivo(file, { maxSizeMB: 12, allowedMimeTypes: ['application/pdf'], allowedExtensions: ['.pdf'] });
    if (!validacion.ok) {
      showToast({ severity: 'error',  summary: 'Error', detail: validacion.msg, life: 3000 });
      e.target.value = '';
      return;
    }

    const tipo_documento = docKeyRef.current;
    const formData = new FormData();

    formData.append('documento', file);
    formData.append('patente', vehiculo.patente);
    formData.append('id_vehiculo', vehiculo.id_vehiculo);
    formData.append('tipo_documento', tipo_documento);

    const yaExiste = !!documentos[tipo_documento];

    try {
      setSubiendoKey(docKeyRef.current);

      let resp;

      if (yaExiste) {
        formData.append('id_documento', documentos[tipo_documento].id_documento);
        resp = await onActualizar?.(tipo_documento, formData); // setProgress
      } else { 
        resp = await onCargar?.(tipo_documento, formData); // setProgress
      }

      if (!resp?.ok) throw new Error(resp?.msg);
      showToast({ severity: 'success',  summary: 'Éxito',  detail: resp?.msg || 'Documento cargado correctamente', life: 3000});

    } catch (error) {
      showToast({ severity: 'error', summary: 'Error', detail: error.message || 'Ocurrió un error al cargar el documento', life: 3000});

    } finally {
      setSubiendoKey(null);
    }

    e.target.value = '';
  };

  const onClickEliminar = async (id_documento) => {
    try {
      const resp = await onEliminar?.(id_documento);
      
      if (!resp?.ok) throw new Error(resp?.msg);
      toast.current?.clear();
      showToast({     
        severity: 'success', 
        summary: 'Eliminado', 
        detail: resp?.msg || 'Documento eliminado correctamente',
        life: 3000
      });

    } catch (error) {
      toast.current?.clear();
      showToast({
        severity: 'error', 
        summary: 'Error',
        detail: error.message || 'No se pudo eliminar el documento',
        life: 3000
      });
    }
  }

  const onClickDescargar = async (key, data) => {
    try {
      const resp = await onDescargar?.(key, data);

      if (!resp?.ok) throw new Error(resp?.msg);

      showToast({ 
        severity: 'success', 
        summary: 'Descarga',
        detail: 'Documento descargado correctamente',
        life: 3000
      });

    } catch (error) {
      showToast({ 
        severity: 'error', 
        summary: 'Error',
        detail: error.message || 'Error al descargar el documento',
        life: 3000
      });
    }
  }

  const renderFila = (doc) => {
    const data = documentos[doc.key];
    const cargado = !!data;
    
    return (
      <div key={doc.key} className={`doc-row ${cargado ? '' : 'doc-row--pendiente'}`}>

        <div className={`doc-file-icon ${cargado ? '' : 'doc-file-icon--vacio'}`}>
          <i className="pi pi-file-pdf" />
        </div>

        <div className="doc-info">

          <p className={`doc-name ${cargado ? '' : 'doc-name--muted'}`}>
            {doc.nombre}
          </p>

          { cargado
            ? <p className="doc-meta">{formatDateTimeFront(data.fecha_subido, '/')} · {data.subido_por}</p>
            : <p className="doc-meta">Sin documento cargado</p>
          }

          { subiendoKey === doc.key && (
            <div className="mt-1">
              <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
              {/* <small className='fs-10'>Subiendo... {progress}%</small> */}
            </div>
          )}
        </div>

        <Tag
          value={cargado ? 'Cargado' : 'Pendiente'}
          severity={cargado ? 'success' : 'secondary'}
          className="doc-tag"
        />

        <div className="doc-actions">
          {cargado && permisos.descargar && (
            <Button
              icon="pi pi-download"
              className="doc-btn doc-btn--download"
              tooltip="Descargar"
              tooltipOptions={{ position: 'top' }}
              onClick={() => onClickDescargar(doc.key, data)}
              disabled={subiendoKey === doc.key}
            />
          )}
          {!cargado && permisos.cargar && (
            <Button
              icon="pi pi-upload"
              className="doc-btn doc-btn--upload"
              tooltip="Cargar documento"
              tooltipOptions={{ position: 'top' }}
              onClick={() => abrirSelector(doc.key)}
              disabled={subiendoKey === doc.key}
            />
          )}
          {cargado && permisos.actualizar && (
            <Button
              icon="pi pi-refresh"
              className="doc-btn doc-btn--upload"
              tooltip="Actualizar documento"
              tooltipOptions={{ position: 'top' }}
              onClick={() => abrirSelector(doc.key)}
              disabled={subiendoKey === doc.key}
            />
          )}
          {cargado && permisos.eliminar && (
            <ConfirmActionDialog
              title="Eliminar Documento"
              message="¿Está seguro de que desea eliminar este documento? Esta acción no se puede deshacer."
              icon="pi pi-exclamation-circle"
              acceptLabel="Eliminar"
              rejectLabel="Cancelar"
              className="deshabilitar-veh"
              onAccept={() => onClickEliminar(data.id_documento)}
              trigger={{
                icon: 'pi pi-trash',
                className: 'doc-btn doc-btn--delete ',
                tooltip: 'Eliminar documento',
                tooltipOptions: { position: 'top' },
                disabled: subiendoKey === doc.key,
              }}
            />
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div className="doc-wrap">
      <Toast ref={toast} position="top-right" />
      <ConfirmDialog />
      
      {/* Input file oculto */}
      <input
        ref={fileRef}
        type="file"
        accept=".pdf"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Header */}
      <div className="doc-header">
        <div className="doc-title-row">
          <div className="doc-header-icon">
            <i className="pi pi-folder-open" />
          </div>
          <div>
            <p className="doc-title">Documentación del vehículo</p>
            <p className="doc-subtitle">{vehiculo.patente} · {vehiculo.marca} {vehiculo.modelo}</p>
          </div>
        </div>
        <span className="doc-counter">{cargados} / {todos.length} documentos</span>
      </div>

      {/* Lista */}
      <div className="doc-list">

        {obligatorios.length > 0 && (
          <>
            <p className="doc-separator">Obligatorios</p>
            {obligatorios.map(renderFila)}
          </>
        )}

        {especificos.length > 0 && (
          <>
            <p className="doc-separator">Específicos del modelo</p>
            {especificos.map(renderFila)}
          </>
        )}

      </div>
    </div>
  );
});

export default DocumentacionVeh;

const getDocumentos = (modelo, tipo) => {
  const esModeloEspecial = MODELOS_ESPECIALES.includes(modelo);
  const esTaxi = tipo === 'TAXI';

  if (esModeloEspecial) {
    return {
      obligatorios: DOCS_COMUNES,
      especificos:  DOCS_MODELOS_ESPECIALES,
    };
  }
  if (esTaxi) {
    return {
      obligatorios: DOCS_COMUNES,
      especificos:  [],
    };
  }
  return {
    obligatorios: DOCS_COMUNES,
    especificos:  [],
  };
};

const DOCS_COMUNES = [
  { key: 'soap',                nombre: 'SOAP'                    },
  { key: 'revision_tecnica',    nombre: 'Revisión técnica'        },
  { key: 'emision_gases',       nombre: 'Emisión de gases'        },
  { key: 'permiso_circulacion', nombre: 'Permiso de circulación'  },
  { key: 'primera_inscripcion', nombre: 'Primera inscripción'     },
  { key: 'padron',              nombre: 'Padrón del vehículo'     },
  { key: 'laminas_seguridad',   nombre: 'Láminas de seguridad'    },
  { key: 'cert_polarizados',    nombre: 'Certificado de polarizados' },
];

const DOCS_MODELOS_ESPECIALES = [
  { key: 'decreto_80',          nombre: 'Decreto 80'                      },
  { key: 'cert_cunas',          nombre: 'Certificado de cuñas'            },
  { key: 'cert_antivuelco',     nombre: 'Certificado antivuelco (Cabina)' },
];

const MODELOS_ESPECIALES = ['SPRINTER 416', 'SPRINTER 515', 'TRANSIT', 'STARIA', 'H1', 'VITO'];
